import {
  PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useSafeContext } from "./useSafeContext";

import de from "../i18n/de";
import it from "../i18n/it";

const translations = {
  de,
  it,
};

export type Language = keyof typeof translations;
const supportedLanguages = Object.freeze(Object.keys(translations));
const LANG_URL_QUERY_KEY = "lang";

const getURLLanguage = (): Language | undefined => {
  const url = new URL(window.location.href);
  const language = url.searchParams.get(LANG_URL_QUERY_KEY);
  if (language && supportedLanguages.includes(language)) {
    return language as Language;
  }
  return;
};

const getStoredLanguage = (): Language | undefined => {
  const language = localStorage.getItem("lang");
  if (language && supportedLanguages.includes(language)) {
    return language as Language;
  }
  return;
};

const getPreferredLanguage = (): Language | undefined => {
  return navigator.languages
    .map((language) => language.split("-")[0]!.toLowerCase())
    .find((language) => supportedLanguages.includes(language)) as
    Language | undefined;
};

const getCurrentLanguage = (): Language => {
  return (
    getURLLanguage() ?? getStoredLanguage() ?? getPreferredLanguage() ?? "de"
  );
};

const isSupportedLanguage = (lang: string): lang is Language => {
  return supportedLanguages.includes(lang);
};

export let currentLanguage: Language = getCurrentLanguage();

export const getCurrentTranslations = (): (typeof translations)[Language] =>
  translations[currentLanguage];

type TranslationContextType = {
  i18n: (typeof translations)[Language];
  currentLanguage: Language;
  setLanguage: (lang: string) => void;
  supportedLanguages: readonly string[];
};

const TranslationContext = createContext<TranslationContextType | undefined>(
  undefined,
);

export const TranslationProvider = ({ children }: PropsWithChildren) => {
  const [language, setLanguage] = useState<Language>(() => currentLanguage);

  const setLang = useCallback((lang: string) => {
    if (isSupportedLanguage(lang)) {
      const url = new URL(window.location.href);

      if (url.searchParams.has(LANG_URL_QUERY_KEY)) {
        url.searchParams.delete(LANG_URL_QUERY_KEY);
        window.history.replaceState({}, "", url);
      }
      currentLanguage = lang;
      setLanguage(lang);
      localStorage.setItem("lang", lang);
    }
  }, []);

  const value = useMemo(
    () => ({
      i18n: translations[language],
      currentLanguage: language,
      setLanguage: setLang,
      supportedLanguages,
    }),
    [language, setLang],
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslations = () => {
  return useSafeContext(TranslationContext, "TranslationContext");
};
